import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// ============================================================================
// SYSTEM COMPONENT: AppButton
// ============================================================================
// This is a system-level wrapper around the UI button primitive.
// It adds cross-cutting concerns: loading state, analytics tracking.
//
// USAGE:
//   import { AppButton } from '@/Components/System';
//
// DO NOT import @/Components/ui/button directly in features.
// ============================================================================

/**
 * Placeholder analytics hook.
 * Replace with actual analytics implementation (e.g., Segment, Mixpanel).
 */
function useAnalytics() {
  const track = React.useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      // Placeholder: Replace with actual analytics implementation
      if (import.meta.env.DEV) {
        console.debug('[Analytics]', event, properties);
      }
      // Example: analytics.track(event, properties);
    },
    []
  );

  return { track };
}

// ----------------------------------------------------------------------------
// Button Variants (mirrors shadcn/ui button)
// ----------------------------------------------------------------------------

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

// ----------------------------------------------------------------------------
// AppButton Props
// ----------------------------------------------------------------------------

export interface AppButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * If true, renders the child as the button element (for composition).
   * Uses Radix Slot for this behavior.
   */
  asChild?: boolean;

  /**
   * Shows a loading spinner and disables the button.
   * The button text is preserved but a spinner is prepended.
   */
  loading?: boolean;

  /**
   * Text to display while loading. Defaults to children.
   */
  loadingText?: string;

  /**
   * Analytics event name to track when the button is clicked.
   * If provided, the event will be sent to the analytics service.
   */
  trackEvent?: string;

  /**
   * Additional properties to include with the analytics event.
   */
  trackProperties?: Record<string, unknown>;
}

// ----------------------------------------------------------------------------
// AppButton Component
// ----------------------------------------------------------------------------

/**
 * AppButton - System-level button component with loading state and analytics.
 *
 * This component wraps the base button primitive and adds:
 * - Loading state with spinner
 * - Analytics tracking on click
 *
 * @example
 * // Basic usage
 * <AppButton>Click me</AppButton>
 *
 * @example
 * // With loading state
 * <AppButton loading={isSubmitting}>Submit</AppButton>
 *
 * @example
 * // With analytics tracking
 * <AppButton
 *   trackEvent="cta_clicked"
 *   trackProperties={{ location: 'header', variant: 'primary' }}
 * >
 *   Get Started
 * </AppButton>
 *
 * @example
 * // Destructive variant
 * <AppButton variant="destructive" loading={isDeleting}>
 *   Delete Item
 * </AppButton>
 */
const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText,
      trackEvent,
      trackProperties,
      onClick,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const { track } = useAnalytics();

    const handleClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        // Track analytics event if specified
        if (trackEvent) {
          track(trackEvent, {
            ...trackProperties,
            timestamp: new Date().toISOString(),
          });
        }

        // Call the original onClick handler
        onClick?.(event);
      },
      [trackEvent, trackProperties, track, onClick]
    );

    // When asChild is true, we render the Slot component
    const Comp = asChild ? Slot : 'button';

    // Determine if the button should be disabled
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          loading && 'cursor-wait'
        )}
        ref={ref}
        disabled={isDisabled}
        onClick={handleClick}
        aria-busy={loading}
        {...props}
      >
        {loading && (
          <Loader2
            className="mr-2 h-4 w-4 animate-spin"
            aria-hidden="true"
          />
        )}
        {loading && loadingText ? loadingText : children}
      </Comp>
    );
  }
);

AppButton.displayName = 'AppButton';

// ----------------------------------------------------------------------------
// Exports
// ----------------------------------------------------------------------------

export { AppButton, buttonVariants };
